import React, { useState, useEffect, useCallback } from 'react';
import { 
  Globe, 
  Loader2,
  ExternalLink,
  CheckCircle,
  Link,
  Unlink,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { useDeployments } from '../../hooks/useDeployments';
import { apiEndpoints } from '../../utils/api';
import { friendlyDomainError } from '../../utils/domainErrors';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import { useFeatures } from '../../hooks/useFeatures';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import FeatureUpgradePrompt from '../common/FeatureUpgradePrompt';

interface DomainsProps {
  workspaceId: string;
  workspace: {
    subdomain?: string;
    custom_domain?: string;
    deployed_url?: string;
  } | null;
}

interface Domain {
  id: string;
  domain_name: string;
  status: string;
  certificate_status?: string;
  cloudfront_status?: string;
  associated_workspace_id?: string;
  assignment_initiated_at?: string; // When assignment/unassignment was initiated (for cooldown)
}

// Map the backend's machine-readable error codes ({detail: {error, message}}) to
// friendly copy; falls back to prose matching for older backend responses.
// Moved to utils/domainErrors so the account Domains section can use it too.
// Re-exported here because existing imports (and tests) reference this path.
export { friendlyDomainError };

const DomainsSection: React.FC<DomainsProps> = ({ workspaceId, workspace }) => {
  const { isReadOnly } = useReadOnlyMode();
  const api = useAuthenticatedApi();
  const { deployments } = useDeployments(workspaceId);
  const { has, loading: featuresLoading } = useFeatures();
  const { openAccountSettings } = useSettingsModal();
  const [loading, setLoading] = useState(false);
  const [currentDomains, setCurrentDomains] = useState<Domain[]>([]);
  const [availableDomains, setAvailableDomains] = useState<Domain[]>([]);
  const [finalizingDomains, setFinalizingDomains] = useState<Domain[]>([]);
  const [assigningDomain, setAssigningDomain] = useState<string | null>(null);
  const [unassigningDomain, setUnassigningDomain] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [confirmAssign, setConfirmAssign] = useState<string | null>(null);
  const [confirmUnassign, setConfirmUnassign] = useState<string | null>(null);

  const COOLDOWN_MINUTES = 15;
  const COOLDOWN_MS = COOLDOWN_MINUTES * 60 * 1000;

  // Check if user has access to custom domains feature
  const hasCustomDomainsFeature = has('custom-domains');

  // Check if workspace has successful deployments
  const hasSuccessfulDeployments = deployments.some(d => d.status === 'COMPLETE');

  // Fetch all user domains
  const fetchDomains = useCallback(async () => {
    // Guard: Don't fetch if user doesn't have the feature
    if (!hasCustomDomainsFeature) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/domains/mine');
      const allDomains = response.domains || [];
      
      // Find ALL domains currently assigned to this workspace
      const assigned = allDomains.filter((d: Domain) => 
        d.associated_workspace_id === workspaceId
      );
      
      // Find domains available for assignment: fully ready (CDN deployed) and not assigned
      const available = allDomains.filter((d: Domain) =>
        d.status === 'registered' &&
        d.certificate_status === 'issued' &&
        d.cloudfront_status === 'deployed' &&
        !d.associated_workspace_id
      );

      // Domains still finalizing their CDN setup — shown disabled until deployed
      const finalizing = allDomains.filter((d: Domain) =>
        d.status === 'registered' &&
        d.certificate_status === 'issued' &&
        !d.associated_workspace_id &&
        !['deployed', 'failed', 'update_failed'].includes(d.cloudfront_status ?? '')
      );

      setCurrentDomains(assigned);
      setAvailableDomains(available);
      setFinalizingDomains(finalizing);
    } catch (err: any) {
      setError('Failed to load domains');
      console.error('Error fetching domains:', err);
    } finally {
      setLoading(false);
    }
  }, [api, workspaceId, hasCustomDomainsFeature]);

  // Cooldown management functions - per domain
  const getDomainLastOperationTime = (domainId: string): number => {
    // Find the domain in current or available lists
    const domain = [...currentDomains, ...availableDomains].find(d => d.id === domainId);
    
    if (domain?.assignment_initiated_at) {
      // Backend sends UTC timestamps without 'Z' suffix
      // We need to explicitly treat them as UTC
      let timestamp = domain.assignment_initiated_at;
      
      // If timestamp doesn't end with 'Z' and doesn't have timezone info, it's UTC from backend
      if (!timestamp.endsWith('Z') && !timestamp.match(/[+-]\d{2}:\d{2}$/)) {
        // Replace the trailing microseconds if present and add Z for UTC
        timestamp = timestamp.replace(/\.\d{6}$/, '') + 'Z';
      }
      
      return new Date(timestamp).getTime();
    }
    
    return 0;
  };

  const checkDomainCooldown = (domainId: string): boolean => {
    const lastOperation = getDomainLastOperationTime(domainId);
    if (!lastOperation) return true;
    
    const elapsed = Date.now() - lastOperation;
    return elapsed >= COOLDOWN_MS;
  };

  const getDomainRemainingCooldown = (domainId: string): number => {
    const lastOperation = getDomainLastOperationTime(domainId);
    if (!lastOperation) return 0;
    
    const elapsed = Date.now() - lastOperation;
    const remaining = COOLDOWN_MS - elapsed;
    return remaining > 0 ? remaining : 0;
  };

  const formatCooldownTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (api && !featuresLoading && hasCustomDomainsFeature) {
      fetchDomains();
    }
  }, [api, featuresLoading, hasCustomDomainsFeature, fetchDomains]);


  // State to force re-renders for countdown updates
  const [, forceUpdate] = useState({});
  
  // Update cooldown timers - trigger re-renders every second to update countdown displays
  useEffect(() => {
    // Check if any domain has a cooldown
    const hasDomainsWithCooldown = [...currentDomains, ...availableDomains].some(
      d => d.assignment_initiated_at && getDomainRemainingCooldown(d.id) > 0
    );
    
    if (hasDomainsWithCooldown) {
      const interval = setInterval(() => {
        // Force re-render to update all countdown displays
        forceUpdate({});
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentDomains, availableDomains]);

  // Poll while any domain's CDN is still settling (finalizing, or an assignment
  // change propagating) so rows enable themselves without a manual refresh
  useEffect(() => {
    const hasSettlingDomains =
      finalizingDomains.length > 0 ||
      currentDomains.some(d => d.cloudfront_status !== 'deployed');

    if (hasSettlingDomains) {
      const interval = setInterval(() => {
        fetchDomains();
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [finalizingDomains, currentDomains, fetchDomains]);

  // Auto-clear messages
  useEffect(() => {
    if (error) {
      // Use 10 seconds for error messages as requested
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleAssignDomain = async (domainId: string, domainName: string) => {
    // Check read-only mode first
    if (isReadOnly) return;

    // Check if workspace has successful deployments first
    if (!hasSuccessfulDeployments) {
      setError('This project must have at least one successful deployment before custom domain assignment. Please deploy your project first.');
      return;
    }

    // Check cooldown first (frontend validation for better UX)
    if (!checkDomainCooldown(domainId)) {
      const remaining = getDomainRemainingCooldown(domainId);
      setError(`Please wait ${formatCooldownTime(remaining)} before making changes to this domain`);
      return;
    }

    // First click - show confirmation state
    if (confirmAssign !== domainId) {
      setConfirmAssign(domainId);
      // Auto-reset confirmation after 3 seconds
      setTimeout(() => setConfirmAssign(null), 3000);
      return;
    }

    // Find domain to assign before try block so it's available in catch
    const domainToAssign = availableDomains.find(d => d.id === domainId);

    try {
      setAssigningDomain(domainId);
      setError(null);
      setSuccess(null);
      setConfirmAssign(null);
      
      // Optimistic update - move domain from available to current with timestamp
      if (domainToAssign) {
        setAvailableDomains(prev => prev.filter(d => d.id !== domainId));
        setCurrentDomains(prev => [...prev, { 
          ...domainToAssign, 
          associated_workspace_id: workspaceId,
          assignment_initiated_at: new Date().toISOString()
        }]);
      }
      
      await api.post(apiEndpoints.domains.assign(domainId), {
        workspace_id: workspaceId
      });
      
      setSuccess(`${domainName} has been assigned to this workspace!`);
      
      // Refresh domains to get the updated state from backend
      await fetchDomains();
    } catch (err: any) {
      setError(friendlyDomainError(err, domainName, 'Failed to assign domain'));

      // Revert on error
      await fetchDomains();
    } finally {
      setAssigningDomain(null);
    }
  };

  const handleUnassignDomain = async (domainId: string, domainName: string) => {
    // Check read-only mode first
    if (isReadOnly) return;

    // Check cooldown first (frontend validation for better UX)
    if (!checkDomainCooldown(domainId)) {
      const remaining = getDomainRemainingCooldown(domainId);
      setError(`Please wait ${formatCooldownTime(remaining)} before making changes to this domain`);
      return;
    }
    
    // First click - show confirmation state
    if (confirmUnassign !== domainId) {
      setConfirmUnassign(domainId);
      // Auto-reset confirmation after 3 seconds
      setTimeout(() => setConfirmUnassign(null), 3000);
      return;
    }

    const domainToUnassign = currentDomains.find(d => d.id === domainId);
    if (!domainToUnassign) return;

    try {
      setUnassigningDomain(true);
      setError(null);
      setSuccess(null);
      setConfirmUnassign(null);
      
      // Optimistic update - move domain from current to available with timestamp
      setCurrentDomains(prev => prev.filter(d => d.id !== domainId));
      
      // Only add to available if it's ready
      if (domainToUnassign.status === 'registered' && 
          domainToUnassign.certificate_status === 'issued') {
        const unassignedDomain = { 
          ...domainToUnassign, 
          associated_workspace_id: undefined,
          assignment_initiated_at: new Date().toISOString()
        };
        setAvailableDomains(prev => [...prev, unassignedDomain]);
      }
      
      await api.post(apiEndpoints.domains.unassign(domainId), {});
      
      setSuccess(`${domainName} has been unassigned from this workspace.`);
      
      // Refresh domains to get the updated state from backend
      await fetchDomains();
    } catch (err: any) {
      setError(friendlyDomainError(err, domainName, 'Failed to unassign domain'));

      // Revert on error
      await fetchDomains();
    } finally {
      setUnassigningDomain(false);
    }
  };

  // Get deployment URLs
  const deploymentUrls = [];
  if (workspace?.deployed_url) {
    deploymentUrls.push({ 
      label: 'Default URL', 
      url: workspace.deployed_url, 
    });
  }
  
  // Add all custom domains
  currentDomains.forEach((domain, index) => {
    deploymentUrls.push({ 
      label: `Custom Domain${currentDomains.length > 1 ? ` ${index + 1}` : ''}`, 
      url: `https://${domain.domain_name}`, 
    });
  });

  // Show loading state only when actually loading
  if (loading && hasCustomDomainsFeature) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  // Show upgrade prompt if feature is not available
  if (!featuresLoading && !hasCustomDomainsFeature) {
    return (
      <div className="space-y-6 pb-12">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Custom Domain
          </h3>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Configure a custom domain for your workspace
          </p>
        </div>

        <FeatureUpgradePrompt
          icon={Globe}
          title="Upgrade to Access Custom Domains"
          description="Custom domains allow you to connect your own domain to your workspace for a professional URL."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      {/* Messages - Show at the top for better visibility */}
      {error && (
        <div className="p-4 bg-semantic-error/10 dark:bg-semantic-error/20 border border-semantic-error/30 dark:border-semantic-error/40 rounded-lg animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-semantic-error flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-semantic-error">Error</p>
              <p className="text-sm text-semantic-error mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 bg-semantic-success/10 dark:bg-semantic-success/20 border border-semantic-success/30 dark:border-semantic-success/40 rounded-lg animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-semantic-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-semantic-success dark:text-brand-primaryDarkMode">{success}</p>
          </div>
        </div>
      )}

      {/* Section Header */}
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
          Custom Domain
        </h3>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          Configure a custom domain for your workspace
        </p>
      </div>

      {/* Deployment Requirement Warning */}
      {!hasSuccessfulDeployments && availableDomains.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
          <div className="flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div className="text-xs text-amber-700 dark:text-amber-300">
              <p className="font-medium mb-1">Deployment Required</p>
              <p>This project must have at least one successful deployment before custom domain assignment. Please publish your project first using the Publish button.</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Message */}
      <div className="bg-neutral-100 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3">
        <div className="flex gap-2">
          <Info className="w-4 h-4 text-neutral-500 dark:text-neutral-400 mt-0.5" />
          <div className="text-xs text-neutral-600 dark:text-neutral-400">
            <p className="mb-1">Custom domains provide a professional URL for your workspace.</p>
            <ul className="space-y-0.5 ml-3">
              <li>• Newly purchased domains may take up to several hours to appear here (first-time setup only)</li>
              <li>• Newly purchased domains finalize for ~10-15 minutes before they can be assigned</li>
              <li>• Domain (un)assignment changes take 15-20 minutes to propagate</li>
              <li>• Domain (un)assignment can be done once every 15 minutes</li>
              <li>• Project must be published at least once before domain assignment</li>
            </ul>
          </div>
        </div>
      </div>


      {/* Current Domains */}
      {currentDomains.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-neutral-700 dark:text-white mb-3">
            Active Custom Domains
          </h4>
          <div className="space-y-2">
            {currentDomains.map((domain) => (
              <div key={domain.id} className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start sm:items-center gap-3">
                    {domain.cloudfront_status === 'deployed' ? (
                      <CheckCircle className="w-5 h-5 text-semantic-success flex-shrink-0 mt-1 sm:mt-0" />
                    ) : (
                      <Loader2 className="w-5 h-5 text-brand-primary animate-spin flex-shrink-0 mt-1 sm:mt-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-base sm:text-lg font-semibold text-neutral-900 dark:text-white break-all">
                        {domain.domain_name}
                      </p>
                      {domain.cloudfront_status === 'deployed' ? (
                        <a
                          href={`https://${domain.domain_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-neutral-600 dark:text-neutral-400 hover:text-brand-primary dark:hover:text-brand-primaryDarkMode hover:underline inline-flex items-center gap-1 mt-1"
                        >
                          Visit site
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                          Update in progress (~15 min)
                        </p>
                      )}
                    </div>
                  </div>
                  {(() => {
                    const cooldown = getDomainRemainingCooldown(domain.id);
                    return (
                      <button
                        onClick={() => handleUnassignDomain(domain.id, domain.domain_name)}
                        disabled={unassigningDomain || cooldown > 0 || isReadOnly}
                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto border ${
                          confirmUnassign === domain.id
                            ? 'bg-semantic-warning text-black hover:bg-semantic-warning/90 border-semantic-warning'
                            : (cooldown > 0 || isReadOnly)
                            ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 border-neutral-200 dark:border-neutral-700'
                            : 'bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
                        }`}
                        title={isReadOnly ? 'Upgrade to unassign domains' : cooldown > 0 ? `This domain is in cooldown. Wait ${formatCooldownTime(cooldown)} before making changes.` : 'Click to unassign this domain'}
                      >
                        {unassigningDomain ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span>Removing</span>
                          </>
                        ) : confirmUnassign === domain.id ? (
                          <>
                            <Unlink className="w-3 h-3" />
                            <span>Click to Confirm</span>
                          </>
                        ) : cooldown > 0 ? (
                          <div className="flex items-center gap-1">
                            <Unlink className="w-3 h-3 opacity-50" />
                            <span className="flex items-center gap-1">
                              <span>Unassign in</span>
                              <span className="font-mono font-medium text-neutral-700 dark:text-white">{formatCooldownTime(cooldown)}</span>
                            </span>
                          </div>
                        ) : (
                          <>
                            <Unlink className="w-3 h-3" />
                            <span>Unassign</span>
                          </>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Domains */}
      {(availableDomains.length > 0 || finalizingDomains.length > 0) && (
        <div>
          <h4 className="text-sm font-medium text-neutral-700 dark:text-white mb-3">
            Available Domains
          </h4>
          <div className="space-y-2">
            {finalizingDomains.map((domain) => (
              <div
                key={domain.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg opacity-75"
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <Globe className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-900 dark:text-white text-sm sm:text-base break-all">
                    {domain.domain_name}
                  </span>
                </div>
                <div
                  className="px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg inline-flex items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700"
                  title="Your domain's CDN configuration is still deploying. It becomes assignable automatically once it finishes."
                >
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Finalizing — ready in a few minutes</span>
                </div>
              </div>
            ))}
            {availableDomains.map((domain) => (
              <div
                key={domain.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg"
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <Globe className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-900 dark:text-white text-sm sm:text-base break-all">
                    {domain.domain_name}
                  </span>
                </div>
                {(() => {
                  const cooldown = getDomainRemainingCooldown(domain.id);
                  const isDisabled = assigningDomain === domain.id || cooldown > 0 || !hasSuccessfulDeployments || isReadOnly;
                  
                  let buttonTitle = 'Click to assign this domain';
                  if (isReadOnly) {
                    buttonTitle = 'Upgrade to assign domains';
                  } else if (!hasSuccessfulDeployments) {
                    buttonTitle = 'Project must be published before domain assignment';
                  } else if (cooldown > 0) {
                    buttonTitle = `This domain is in cooldown. Wait ${formatCooldownTime(cooldown)} before making changes.`;
                  }
                  
                  return (
                    <button
                      onClick={() => handleAssignDomain(domain.id, domain.domain_name)}
                      disabled={isDisabled}
                      className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto ${
                        confirmAssign === domain.id
                          ? 'bg-semantic-warning text-black hover:bg-semantic-warning/90'
                          : isDisabled
                          ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400'
                          : 'bg-brand-primary hover:bg-brand-primaryDark text-black'
                      }`}
                      title={buttonTitle}
                    >
                      {assigningDomain === domain.id ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Assigning</span>
                        </>
                      ) : confirmAssign === domain.id ? (
                        <>
                          <Link className="w-3 h-3" />
                          <span>Click to Confirm</span>
                        </>
                      ) : !hasSuccessfulDeployments ? (
                        <div className="flex items-center gap-1">
                          <Link className="w-3 h-3 opacity-50" />
                          <span>Publish Project First</span>
                        </div>
                      ) : cooldown > 0 ? (
                        <div className="flex items-center gap-1">
                          <Link className="w-3 h-3 opacity-50" />
                          <span className="flex items-center gap-1">
                            <span>Assign in</span>
                            <span className="font-mono font-medium text-neutral-700 dark:text-white">{formatCooldownTime(cooldown)}</span>
                          </span>
                        </div>
                      ) : (
                        <>
                          <Link className="w-3 h-3" />
                          <span>Use This Domain</span>
                        </>
                      )}
                    </button>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Available Domains */}
      {currentDomains.length === 0 && availableDomains.length === 0 && finalizingDomains.length === 0 && (
        <div className="text-center py-6 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
          <Globe className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-600 dark:text-neutral-400 mb-3">
            No domains available for this workspace
          </p>
          {isReadOnly ? (
            <button
              disabled
              className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 rounded-lg font-medium cursor-not-allowed"
              title="Upgrade to manage domains"
            >
              <Globe className="w-4 h-4" />
              Manage Domains
            </button>
          ) : (
            <button
              onClick={() => openAccountSettings('domains')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark text-black rounded-lg font-medium transition-colors"
            >
              <Globe className="w-4 h-4" />
              Manage Domains
            </button>
          )}
        </div>
      )}

      {/* Deployment URLs */}
      <div className="mb-8">
        <h4 className="text-sm font-medium text-neutral-700 dark:text-white mb-3">
          Deployment URLs
        </h4>
        <div className="space-y-2">
          {deploymentUrls.map((deployment, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg border ${
                'bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                    {deployment.label}
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <a
                      href={deployment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral-700 dark:text-white hover:text-brand-primary dark:hover:text-brand-primaryDarkMode hover:underline inline-flex items-center gap-1 text-xs sm:text-sm font-mono break-all"
                    >
                      {deployment.url}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(deployment.url);
                    setCopiedUrl(deployment.url);
                    setTimeout(() => setCopiedUrl(null), 2000);
                  }}
                  className="p-2 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors relative self-start sm:self-center"
                  title="Copy URL"
                >
                  {copiedUrl === deployment.url ? (
                    <CheckCircle className="w-4 h-4 text-semantic-success" />
                  ) : (
                    <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DomainsSection;